import React, {
  useEffect,
  useCallback,
  useState,
  useContext,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import ReactModal from 'react-modal';
import { SisuContext } from '../../Context';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import {
  ThemeOverride,
  ThemeOptions,
} from '@wix/da-react-context/pkg/ThemeContext';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';

import CloseButtonThin from '../../Primitives/CloseButton';
import CloseIconBold from '@wix/da-ds/pkg/Icons/CloseBold';
import IconButton from '../../Button/IconButton';
import {
  useBodyScrollLock,
  BodyScrollOptions,
} from '@wix/da-ds/pkg/hooks/useBodyScrollLock';
import { PopupContext } from '@wix/da-ds/pkg/popups/PopupContext';
import {
  useVisualViewport,
  VisualViewport,
} from '@wix/da-ds/pkg/hooks/useVisualViewport';
import ddt from '@wix/da-ddt';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';

import s from './Modal.scss';

export interface Props {
  isOpen?: boolean;
  isLoading?: boolean;
  className?: string;
  withCloseButton?: boolean;
  closeBtnClassName?: string;
  closeBtnButtonClassName?: string;
  closeBtnSize?: IconSize;
  contentLabel?: string; // adds aria-label to the modal
  contentClassName?: string;
  overlayClassName?: string;
  portalClassName?: string;
  overlayStyle?: React.CSSProperties;
  width?: string | number;
  height?: string | number;
  disableAriaHide?: boolean; // for storybook
  forceTheme?: ThemeOptions;
  allowThemeSkins?: boolean;
  onRequestClose?(): void;
  shouldCloseOnOverlayClick?: boolean;
  shouldReturnFocusAfterClose?: boolean;
  bodyScrollLockOptions?: BodyScrollOptions;
  useMobileFixedHeight?: boolean; // If you doing a mobile popup you probably want this...
}

const Modal: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen = false,
  isLoading = false,
  onRequestClose,
  width,
  height,
  className,
  closeBtnClassName,
  closeBtnButtonClassName,
  closeBtnSize,
  contentClassName = className,
  contentLabel,
  disableAriaHide,
  withCloseButton = true,
  shouldCloseOnOverlayClick = true,
  shouldReturnFocusAfterClose,
  overlayStyle,
  overlayClassName,
  portalClassName,
  forceTheme,
  allowThemeSkins,
  bodyScrollLockOptions,
  children,
  useMobileFixedHeight,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const style: { content: React.CSSProperties; overlay?: React.CSSProperties } =
    {
      content: {
        width,
        height,
      },
      overlay: overlayStyle,
    };

  // Mobile fixed to viewport fixes
  const [visualViewport, setVisualViewport] = useState<
    VisualViewport | undefined
  >();
  const viewportHandler = useCallback((v: VisualViewport) => {
    setVisualViewport(v);
  }, []);
  useVisualViewport(
    viewportHandler,
    isMobile === true && useMobileFixedHeight === true
  );
  const contentStyle: React.CSSProperties = {};
  // Apply fixed height for mobile if requested, helper for fixing Safari being lame
  if (isMobile && visualViewport && !height) {
    style.content.height = visualViewport.height;
    style.content.transform = `translate(${visualViewport.offsetLeft}px,${visualViewport.offsetTop}px)`;
    style.content.scale = visualViewport.scale;
    // Calculate positions from top left
    style.content.top = 0;
    style.content.left = 0;
    style.content.position = 'fixed';
    // Set the max height of the content, fixes content expanding beyond viewport
    contentStyle.maxHeight = visualViewport.height;
    ddt.log('popup', 'Adjusting to viewport offset', visualViewport);
  }

  // Mobile body scroll locking
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { onLock, onUnlock, setScrollableRef, scrollableElementRef } =
    useBodyScrollLock(bodyScrollLockOptions);

  useEffect(() => {
    const focusedEl = document.activeElement;
    const contentEl = contentRef.current;
    const isFocused = el => el === focusedEl || el.contains(focusedEl);
    const needsFocus = contentEl && (!focusedEl || !isFocused(contentEl));
    let focusTimeout;

    if (needsFocus) {
      // useEffect is supposed to be invoked when everything mounted, but without this timeout something else steals the focus
      focusTimeout = setTimeout(() => contentEl!.focus(), 100);
    }

    return () => focusTimeout && clearTimeout(focusTimeout);
  }, []);

  const setContentRef = (instance: HTMLDivElement | null) => {
    contentRef.current = instance;

    // It might happen that the Popup renders a component that calls setScrollableRef on its own, such as ScrollableContent.
    // In this case we avoid updating the ref because that would cause an incorrect re-lock to the modal content element.
    if (!scrollableElementRef.current) {
      setScrollableRef(instance);
    }
  };

  const onClose = useCallback(() => {
    onRequestClose && onRequestClose();
  }, [onRequestClose]);

  return (
    <ReactModal
      isOpen={isOpen}
      onAfterOpen={onLock}
      onRequestClose={onClose}
      onAfterClose={onUnlock}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
      className={classnames(s['modal'], className)}
      overlayClassName={classnames(s['overlay'], overlayClassName)}
      portalClassName={classnames('ReactModalPortal', portalClassName)}
      contentLabel={contentLabel}
      style={style}
      ariaHideApp={!disableAriaHide}
      contentRef={setContentRef}
    >
      <ThemeOverride
        className={classnames(
          !allowThemeSkins && 'no-theme-skin',
          contentClassName
        )}
        style={contentStyle}
      >
        {withCloseButton && (
          <SisuContext.Consumer>
            {isSisu =>
              isSisu ? (
                <CloseButtonThin
                  className={classnames(
                    s['sisu-close-button'],
                    closeBtnClassName
                  )}
                  onClick={onClose}
                />
              ) : (
                <IconButton
                  className={classnames(
                    s['close-button'],
                    closeBtnButtonClassName
                  )}
                  onClick={onClose}
                  aria-label={t('common.close')}
                  noHoverEffect
                >
                  <CloseIconBold
                    className={closeBtnClassName}
                    size={closeBtnSize}
                    disableDefaultColors
                  />
                </IconButton>
              )
            }
          </SisuContext.Consumer>
        )}
        <ErrorBoundary debugComponent="Modal">
          <PopupContext.Provider
            value={{
              isInPopup: true,
              setScrollableContentRef: setScrollableRef,
            }}
          >
            {isLoading ? <LoadingIndicator /> : children}
          </PopupContext.Provider>
        </ErrorBoundary>
      </ThemeOverride>
    </ReactModal>
  );
};
Modal.displayName = 'Modal';

export default Modal;
